import {
  QUESTIONNAIRE_LAUNCH_MODE,
  TREATMENT_LAUNCH_SOURCE_KIND,
} from "./launchResolver";

const compact = (parts) => parts.filter(Boolean).map(String).join(":");

export const resolveQuestionnaireRuntimeAdapter = ({
  templateId = null,
  runtimeProgramId = null,
  runtimeCustomProgramId = null,
  runtimeApiBaseUrl = null,
  previewProgramId = null,
  previewCustomProgramId = null,
  previewCapabilityToken = null,
  previewSnapshotId = null,
  previewSnapshotChecksum = null,
} = {}) => {
  const liveSources = [
    runtimeProgramId && {
      kind: TREATMENT_LAUNCH_SOURCE_KIND.program,
      id: runtimeProgramId,
    },
    runtimeCustomProgramId && {
      kind: TREATMENT_LAUNCH_SOURCE_KIND.customProgram,
      id: runtimeCustomProgramId,
    },
  ].filter(Boolean);
  const previewSources = [
    previewProgramId && {
      kind: TREATMENT_LAUNCH_SOURCE_KIND.program,
      id: previewProgramId,
    },
    previewCustomProgramId && {
      kind: TREATMENT_LAUNCH_SOURCE_KIND.customProgram,
      id: previewCustomProgramId,
    },
  ].filter(Boolean);

  if (liveSources.length > 1 || previewSources.length > 1) {
    throw new Error("Questionnaire runtime has multiple competing sources.");
  }
  if (liveSources.length && previewSources.length) {
    throw new Error("Live and preview questionnaire runtimes cannot be combined.");
  }

  if (previewSources.length) {
    if (!previewCapabilityToken) {
      throw new Error("Preview capability is required for treatment preview.");
    }
    if (
      !previewSnapshotId || !previewSnapshotChecksum
    ) {
      throw new Error("Preview capability requires immutable snapshot identity.");
    }
    const source = previewSources[0];
    const capabilityIdentity = compact([
      previewSnapshotId,
      previewSnapshotChecksum,
    ]);
    return Object.freeze({
      mode: QUESTIONNAIRE_LAUNCH_MODE.treatmentPreview,
      sourceKind: source.kind,
      sourceId: source.id,
      isLegacy: false,
      isPreview: true,
      hasAuthority: true,
      initializeLegacy: false,
      persistenceKey: compact([
        "welliemd",
        "preview",
        source.kind,
        capabilityIdentity,
      ]),
    });
  }

  if (liveSources.length) {
    if (!runtimeApiBaseUrl) {
      throw new Error("Tenant runtime API endpoint is unavailable.");
    }
    const source = liveSources[0];
    return Object.freeze({
      mode: QUESTIONNAIRE_LAUNCH_MODE.treatmentLive,
      sourceKind: source.kind,
      sourceId: source.id,
      isLegacy: false,
      isPreview: false,
      hasAuthority: true,
      runtimeApiBaseUrl,
      initializeLegacy: false,
      persistenceKey: compact([
        "welliemd",
        "runtime",
        source.kind,
        source.id,
      ]),
    });
  }

  if (!templateId) {
    throw new Error("Legacy questionnaire template ID is required.");
  }
  return Object.freeze({
    mode: QUESTIONNAIRE_LAUNCH_MODE.legacy,
    sourceKind: null,
    sourceId: null,
    isLegacy: true,
    isPreview: false,
    hasAuthority: false,
    initializeLegacy: true,
    // Preserve the production key exactly for old questionnaire compatibility.
    persistenceKey: `welliemd_draft_${templateId}`,
  });
};
